import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import RevenueSection from './components/RevenueSection';
import CostBreakdownSection from './components/CostBreakdownSection';
import PaymentTermsSection from './components/PaymentTermsSection';
import WorkingCapitalSection from './components/WorkingCapitalSection';
import MarginAnalysisSection from './components/MarginAnalysisSection';
import CashflowProjectionSection from './components/CashflowProjectionSection';
import FinancialHealthIndicator from './components/FinancialHealthIndicator';

const FinancialAnalysisForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    monthlyRevenue: {},
    rawMaterialCost: '',
    packagingCost: '',
    laborCost: '',
    utilityCost: '',
    transportationCost: '',
    maintenanceCost: '',
    marketingCost: '',
    administrativeCost: '',
    otherOperationalCost: '',
    supplierPaymentCycle: '',
    avgSupplierPayment: '',
    activeSupplierCount: '',
    paymentDelayDays: '',
    customerPaymentTerms: '',
    avgOutstandingReceivables: '',
    onTimePaymentPercentage: '',
    badDebt: '',
    currentAssets: '',
    currentLiabilities: '',
    cashReserve: '',
    dailyOperatingCost: '',
    rawMaterialInventory: '',
    finishedGoodsInventory: '',
    totalRevenue: '',
    costOfGoodsSold: '',
    totalOperatingExpenses: '',
    totalExpenses: '',
    monthlyInflow: '',
    monthlyOutflow: '',
    currentMonthlyCashflow: '',
    growthScenario: '',
    projectedGrowthRate: '',
    scaleUpInvestment: '',
    monthlyFixedCosts: '',
    variableCostPerUnit: '',
    pricePerUnit: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, subField, value) => {
    if (subField) {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev?.[field],
          [subField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    if (errors?.[field] || errors?.[`${field}.${subField}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        delete newErrors?.[`${field}.${subField}`];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.supplierPaymentCycle) {
      newErrors.supplierPaymentCycle = 'Siklus pembayaran pemasok wajib diisi';
    }

    if (!formData?.avgSupplierPayment) {
      newErrors.avgSupplierPayment = 'Rata-rata pembayaran pemasok wajib diisi';
    }

    if (!formData?.customerPaymentTerms) {
      newErrors.customerPaymentTerms = 'Syarat pembayaran pelanggan wajib diisi';
    }

    if (!formData?.currentAssets) {
      newErrors.currentAssets = 'Aset lancar wajib diisi';
    }

    if (!formData?.currentLiabilities) {
      newErrors.currentLiabilities = 'Kewajiban lancar wajib diisi';
    }

    if (!formData?.cashReserve) {
      newErrors.cashReserve = 'Cadangan kas wajib diisi';
    }

    if (!formData?.dailyOperatingCost) {
      newErrors.dailyOperatingCost = 'Biaya operasional harian wajib diisi';
    }

    if (!formData?.totalRevenue) {
      newErrors.totalRevenue = 'Total pendapatan wajib diisi';
    }

    if (!formData?.costOfGoodsSold) {
      newErrors.costOfGoodsSold = 'HPP wajib diisi';
    }

    if (!formData?.totalOperatingExpenses) {
      newErrors.totalOperatingExpenses = 'Total biaya operasional wajib diisi';
    }

    if (!formData?.totalExpenses) {
      newErrors.totalExpenses = 'Total pengeluaran wajib diisi';
    }

    if (!formData?.monthlyInflow) {
      newErrors.monthlyInflow = 'Arus kas masuk wajib diisi';
    }

    if (!formData?.monthlyOutflow) {
      newErrors.monthlyOutflow = 'Arus kas keluar wajib diisi';
    }

    if (!formData?.currentMonthlyCashflow) {
      newErrors.currentMonthlyCashflow = 'Arus kas bersih wajib diisi';
    }

    if (!formData?.growthScenario) {
      newErrors.growthScenario = 'Skenario pertumbuhan wajib dipilih';
    }

    if (!formData?.projectedGrowthRate) {
      newErrors.projectedGrowthRate = 'Tingkat pertumbuhan wajib diisi';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Financial Analysis Data:', formData);
      setIsSubmitting(false);
      navigate('/market-assessment');
    }, 1500);
  };

  const handleSaveDraft = () => {
    console.log('Draft saved:', formData);
    alert('Data berhasil disimpan sebagai draft');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={5} completedSteps={[1, 2, 3, 4]} />
      <AssessmentNavigation completedModules={['supplier', 'operational', 'equipment']} />

      <main className="with-assessment-nav">
        <div className="container-executive section-spacing">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 md:mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="DollarSign" size={32} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2">Analisis Keuangan</h1>
                  <p className="text-base md:text-lg text-muted-foreground">
                    Evaluasi komprehensif kesehatan keuangan dan proyeksi arus kas untuk scale-up
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                  <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
                  <span className="text-sm font-medium">Analisis Margin</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-success/10 rounded-lg">
                  <Icon name="Wallet" size={18} color="var(--color-success)" />
                  <span className="text-sm font-medium">Proyeksi Arus Kas</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-warning/10 rounded-lg">
                  <Icon name="Activity" size={18} color="var(--color-warning)" />
                  <span className="text-sm font-medium">Kesehatan Keuangan</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
              <RevenueSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <CostBreakdownSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <PaymentTermsSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <WorkingCapitalSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <MarginAnalysisSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <CashflowProjectionSection
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <FinancialHealthIndicator formData={formData} />

              <div className="card">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/equipment-analysis')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    className="flex-1"
                  >
                    Kembali ke Analisis Peralatan
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSaveDraft}
                    iconName="Save"
                    iconPosition="left"
                    className="flex-1"
                  >
                    Simpan Draft
                  </Button>

                  <Button
                    type="submit"
                    variant="default"
                    loading={isSubmitting}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="flex-1"
                  >
                    {isSubmitting ? 'Menyimpan...' : 'Lanjut ke Penilaian Pasar'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinancialAnalysisForm;